import * as React from 'react';
import {
	DatePicker,
	DefaultButton,
	DetailsList,
	DetailsListLayoutMode,
	Dialog,
	DialogFooter,
	DialogType,
	Dropdown,
	IColumn,
	IDropdownOption,
	MessageBar,
	MessageBarType,
	PrimaryButton,
	Selection,
	SelectionMode,
	Stack,
	Text,
	TextField,
} from '@fluentui/react';
import { GetRoomAvailabilitySlots, saveRoomAvailabilitySlot, deleteRoomAvailabilitySlot } from '../../../api/rooms';
import { IRoomAvailabilitySlot } from '../../../types/SiteLocation.types';

type RoomAvailabilitySlotsProps = {
	roomId: number;
};

const toDateOrUndefined = (value: unknown): Date | undefined => {
	if (!value) {
		return undefined;
	}

	if (value instanceof Date) {
		return Number.isNaN(value.getTime()) ? undefined : value;
	}

	const parsed = new Date(value as string);
	return Number.isNaN(parsed.getTime()) ? undefined : parsed;
};

const normalizeSlotDates = (slot: IRoomAvailabilitySlot): IRoomAvailabilitySlot => ({
	...slot,
	key: slot.key ?? `${slot.Day}-${slot.TimeStart}-${slot.TimeEnd}`,
	EffectiveFrom: slot.EffectiveFrom ? new Date(slot.EffectiveFrom) : new Date(),
	EffectiveTo: toDateOrUndefined(slot.EffectiveTo),
});

const defaultNewSlotFormState: IRoomAvailabilitySlot = {
	key: '',
	Day: 1,
	TimeStart: '',
	TimeEnd: '',
	EffectiveFrom: new Date(),
	EffectiveTo: undefined
};

const dayOptions: IDropdownOption[] = [
	{ key: 1, text: 'Monday' },
	{ key: 2, text: 'Tuesday' },
	{ key: 3, text: 'Wednesday' },
	{ key: 4, text: 'Thursday' },
	{ key: 5, text: 'Friday' },
	{ key: 6, text: 'Saturday' },
	{ key: 7, text: 'Sunday' },
];

const getDayLabel = (day: number): string => {
	const option = dayOptions.find((item) => item.key === day);
	return option?.text ?? day.toString();
};

const RoomAvailabilitySlots: React.FC<RoomAvailabilitySlotsProps> = ({ roomId }) => {
	const [slots, setSlots] = React.useState<IRoomAvailabilitySlot[]>([]);
	const [isLoading, setIsLoading] = React.useState<boolean>(false);
	const [isNewDialogOpen, setIsNewDialogOpen] = React.useState<boolean>(false);
	const [isEditDialogOpen, setIsEditDialogOpen] = React.useState<boolean>(false);
	const [newSlot, setNewSlot] = React.useState<IRoomAvailabilitySlot>(defaultNewSlotFormState);
	const [editSlot, setEditSlot] = React.useState<IRoomAvailabilitySlot>(defaultNewSlotFormState);
	const [selectedSlot, setSelectedSlot] = React.useState<IRoomAvailabilitySlot | undefined>(undefined);

	const selection = React.useMemo(
		() =>
			new Selection({
				onSelectionChanged: () => {
					const selectedItem = selection.getSelection()[0] as IRoomAvailabilitySlot | undefined;
					setSelectedSlot(selectedItem);
				},
			}),
		[]
	);

	const loadRoomAvailabilitySlots = React.useCallback(async () => {
		setIsLoading(true);

		try {
			const response = await GetRoomAvailabilitySlots(roomId);
			const data = await response.json();
			setSlots((data as IRoomAvailabilitySlot[]).map(normalizeSlotDates));
		} finally {
			setIsLoading(false);
		}
	}, [roomId]);

	React.useEffect(() => {
		void loadRoomAvailabilitySlots();
	}, [loadRoomAvailabilitySlots]);

	React.useEffect(() => {
		selection.setItems(slots, true);
		setSelectedSlot(undefined);
	}, [selection, slots]);

	const columns = React.useMemo<IColumn[]>(
		() => [
			{
				key: 'day',
				name: 'Day',
				fieldName: 'Day',
				minWidth: 70,
				maxWidth: 70,
				isResizable: true,
				onRender: (item: IRoomAvailabilitySlot) => getDayLabel(item.Day),
			},
			{
				key: 'description',
				name: 'Description',
				fieldName: 'Description',
				minWidth: 180,
				isResizable: true,
			},
			{
				key: 'timeStart',
				name: 'Time Start',
				fieldName: 'TimeStart',
				minWidth: 70,
				isResizable: true,
			},
			{
				key: 'timeEnd',
				name: 'Time End',
				fieldName: 'TimeEnd',
				minWidth: 70,
				isResizable: true,
			},
			{
				key: 'effectiveFrom',
				name: 'Effective From',
				fieldName: 'EffectiveFrom',
				minWidth: 100,
				isResizable: true,
				onRender: (item: IRoomAvailabilitySlot) =>
					item.EffectiveFrom ? new Date(item.EffectiveFrom).toLocaleDateString() : '',
			},
			{
				key: 'effectiveTo',
				name: 'Effective To',
				fieldName: 'EffectiveTo',
				minWidth: 100,
				isResizable: true,
				onRender: (item: IRoomAvailabilitySlot) =>
					item.EffectiveTo ? new Date(item.EffectiveTo).toLocaleDateString() : '',
			},
		],
		[]
	);

	const openNewDialog = () => {
		setNewSlot(defaultNewSlotFormState);
		setIsNewDialogOpen(true);
	};

	const closeNewDialog = () => {
		setIsNewDialogOpen(false);
	};

	const openEditDialog = () => {
		if (!selectedSlot) {
			return;
		}

		setEditSlot(normalizeSlotDates(selectedSlot));
		setIsEditDialogOpen(true);
	};

	const closeEditDialog = () => {
		setIsEditDialogOpen(false);
	};

	const onSaveNewSlot = async () => {
		await saveRoomAvailabilitySlot(roomId, newSlot);
		setIsNewDialogOpen(false);
		await loadRoomAvailabilitySlots();
	};

	const onSaveEditedSlot = async () => {
		await saveRoomAvailabilitySlot(roomId, editSlot);
		setIsEditDialogOpen(false);
		await loadRoomAvailabilitySlots();
	};

	const onDeleteSlot = async () => {
		if (!selectedSlot) {
			return;
		}
		if (selectedSlot.Id === undefined || selectedSlot.Id === -1) {
			return;
		}
		await deleteRoomAvailabilitySlot(roomId, selectedSlot.Id);
		await loadRoomAvailabilitySlots();
	};

	return (
		<Stack tokens={{ childrenGap: 12 }}>
			<div style={{marginTop: 8, marginBottom: 8}}>
				<MessageBar messageBarType={MessageBarType.info}>
					Here we define when a room is not available for booking. For example, if a room is not available on Mondays from 9 AM to 5 PM, you would create a slot with day=1 (Monday), timeStart=09:00, and timeEnd=17:00. You can also specify effective dates for when this availability applies.
				</MessageBar>
			</div>
			<Stack style={{marginTop: 8, marginBottom: 8}} horizontal horizontalAlign="space-between" verticalAlign="center">
				<Stack horizontal tokens={{ childrenGap: 8 }}>
					<PrimaryButton text="New" iconProps={{iconName: "DateTime"}} onClick={openNewDialog} />
					<DefaultButton text="Edit" iconProps={{iconName: "Edit"}} onClick={openEditDialog} disabled={!selectedSlot} />
					<DefaultButton text="Delete" iconProps={{iconName: "Delete"}} onClick={onDeleteSlot} disabled={!selectedSlot} />
				</Stack>
			</Stack>

			{slots.length === 0 && !isLoading && (
				<div style={{marginTop: 8, marginBottom: 8}}>
					<MessageBar messageBarType={MessageBarType.info}>No room availability slots created for this room.</MessageBar>
				</div>
			)}

			{slots.length > 0 && (
				<div style={{marginTop: 8, marginBottom: 8}}>
					<DetailsList
						items={slots}
						columns={columns}
						selection={selection}
						selectionMode={SelectionMode.single}
						layoutMode={DetailsListLayoutMode.justified}
						isHeaderVisible
					/>
				</div>
			)}

			{isLoading && (
				<div style={{marginTop: 8, marginBottom: 8}}>
					<MessageBar messageBarType={MessageBarType.info}>Loading room availability slots...</MessageBar>
				</div>
			)}

			<Dialog
				hidden={!isNewDialogOpen}
				onDismiss={closeNewDialog}
				dialogContentProps={{
					type: DialogType.normal,
					title: 'New Room Availability Slot',
				}}
				minWidth={520}
			>
				<Stack tokens={{ childrenGap: 10 }}>
					<Dropdown
						label="Day"
						options={dayOptions}
						selectedKey={newSlot.Day}
						onChange={(_, option) =>
							setNewSlot((prev) => ({ ...prev, Day: Number(option?.key ?? 1) }))
						}
					/>
					<span style={{ fontStyle: 'italic', marginTop: 0, fontSize: 12 }}>Select the day of the week for this availability slot.</span>
					<TextField
						label="Description"
						onGetErrorMessage={(value) => {
							if (!value || value.trim() === '') {
								return 'Description is required.';
							}
							return '';
						}}
						value={newSlot.Description}
						onChange={(_, value) =>
							setNewSlot((prev) => ({ ...prev, Description: value ?? '' }))
						}
					/>
					<TextField
						label="Time Start"
						onGetErrorMessage={(value) => {
							const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
							if (!timePattern.test(value ?? '')) {
								return 'Invalid time format. Please use HH:mm.';
							}
							return '';
						}}
						value={newSlot.TimeStart}
						onChange={(_, value) =>
							setNewSlot((prev) => ({ ...prev, TimeStart: value ?? '' }))
						}
					/>
					<span style={{ fontStyle: 'italic', marginTop: 0, fontSize: 12 }}>Enter the start time in HH:mm format. E.g. 09:00</span>
					<TextField
						label="Time End"
						onGetErrorMessage={(value) => {
							const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
							if (!timePattern.test(value ?? '')) {
								return 'Invalid time format. Please use HH:mm.';
							}
							return '';
						}}
						value={newSlot.TimeEnd}
						onChange={(_, value) =>
							setNewSlot((prev) => ({ ...prev, TimeEnd: value ?? '' }))
						}
					/>
					<span style={{ fontStyle: 'italic', marginTop: 0, fontSize: 12 }}>Enter the end time in HH:mm format. E.g. 17:00</span>
					<DatePicker
						label="Effective From"
						value={newSlot.EffectiveFrom}
						onSelectDate={(date: Date | null | undefined) =>
							setNewSlot((prev) => ({ ...prev, EffectiveFrom: date ?? new Date() }))
						}
					/>
					<DatePicker
						label="Effective To"
						value={newSlot.EffectiveTo}
						onSelectDate={(date: Date | null | undefined) => {
							setNewSlot((prev) => ({ ...prev, EffectiveTo: date ?? undefined }));
						}}
					/>
					<span style={{ fontStyle: 'italic', marginTop: 0, fontSize: 12 }}>Optional: Select an end date for the availability slot to end. Leave empty for no end date.</span>
				</Stack>
				<DialogFooter>
					<PrimaryButton text="Save" onClick={onSaveNewSlot} />
					<DefaultButton text="Cancel" onClick={closeNewDialog} />
				</DialogFooter>
			</Dialog>

			<Dialog
				hidden={!isEditDialogOpen}
				onDismiss={closeEditDialog}
				dialogContentProps={{
					type: DialogType.normal,
					title: 'Edit Room Availability Slot',
				}}
				minWidth={520}
			>
				<Stack tokens={{ childrenGap: 10 }}>
					<Dropdown
						label="Day"
						options={dayOptions}
						selectedKey={editSlot.Day}
						onChange={(_, option) =>
							setEditSlot((prev) => ({ ...prev, Day: Number(option?.key ?? 1) }))
						}
					/>
					<span style={{ fontStyle: 'italic', marginTop: 0, fontSize: 12 }}>Select the day of the week for this availability slot.</span>
					<TextField
						label="Description"
						onGetErrorMessage={(value) => {
							if (!value || value.trim() === '') {
								return 'Description is required.';
							}
							return '';
						}}
						value={editSlot.Description}
						onChange={(_, value) =>
							setEditSlot((prev) => ({ ...prev, Description: value ?? '' }))
						}
					/>
					<TextField
						label="Time Start"
						onGetErrorMessage={(value) => {
							const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
							if (!timePattern.test(value ?? '')) {
								return 'Invalid time format. Please use HH:mm.';
							}
							return '';
						}}
						value={editSlot.TimeStart}
						onChange={(_, value) =>
							setEditSlot((prev) => ({ ...prev, TimeStart: value ?? '' }))
						}
					/>
					<span style={{ fontStyle: 'italic', marginTop: 0, fontSize: 12 }}>Enter the start time in HH:mm format. E.g. 09:00</span>
					<TextField
						label="Time End"
						onGetErrorMessage={(value) => {
							const timePattern = /^([01]\d|2[0-3]):([0-5]\d)$/;
							if (!timePattern.test(value ?? '')) {
								return 'Invalid time format. Please use HH:mm.';
							}
							return '';
						}}
						value={editSlot.TimeEnd}
						onChange={(_, value) =>
							setEditSlot((prev) => ({ ...prev, TimeEnd: value ?? '' }))
						}
					/>
					<span style={{ fontStyle: 'italic', marginTop: 0, fontSize: 12 }}>Enter the end time in HH:mm format. E.g. 17:00</span>
					<DatePicker
						label="Effective From"
						value={editSlot.EffectiveFrom}
						onSelectDate={(date: Date | null | undefined) =>
							setEditSlot((prev) => ({ ...prev, EffectiveFrom: date ?? new Date() }))
						}
					/>
					<DatePicker
						label="Effective To"
						value={editSlot.EffectiveTo}
						onSelectDate={(date: Date | null | undefined) => {
							setEditSlot((prev) => ({ ...prev, EffectiveTo: date ?? undefined }));
						}}
					/>
					<span style={{ fontStyle: 'italic', marginTop: 0, fontSize: 12 }}>Optional: Select an end date for the availability slot to end. Leave empty for no end date.</span>
				</Stack>
				<DialogFooter>
					<PrimaryButton text="Save" onClick={onSaveEditedSlot} />
					<DefaultButton text="Cancel" onClick={closeEditDialog} />
				</DialogFooter>
			</Dialog>
		</Stack>
	);
};

export default RoomAvailabilitySlots;
